import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { Eyebrow } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zorem-desk-token";
const STATUSES = ["received", "under_review", "verification", "cleared", "declined"];
const STATUS_COLORS = {
  received: "text-zinc-300", under_review: "text-amber-300", verification: "text-sky-300",
  cleared: "text-emerald-300", declined: "text-red-400",
};

const inputCls =
  "w-full bg-[#0A0A0B] card-line px-4 py-3.5 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-[#C9A227]/60 transition-colors";

export default function Desk() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [mandates, setMandates] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/desk/mandates`, { headers: { Authorization: `Bearer ${t}` } });
      setMandates(res.data);
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    document.title = "Desk — Zorem Commodities";
    let el = document.head.querySelector('meta[name="robots"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "robots");
      document.head.appendChild(el);
    }
    el.setAttribute("content", "noindex, nofollow");
    return () => el.setAttribute("content", "index, follow");
  }, []);
  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await axios.post(`${API}/desk/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/desk/mandates/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setMandates((m) => m.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Status updated to ${status.replace("_", " ")}`);
    } catch {
      toast.error("Update failed");
    }
  };

  if (!token) {
    return (
      <div data-testid="desk-login" className="bg-[#0A0A0B] min-h-screen pt-44 pb-24">
        <div className="max-w-sm mx-auto px-6">
          <Eyebrow>Private Desk</Eyebrow>
          <h1 className="font-display text-2xl text-white tracking-wide">Mandate Dashboard</h1>
          <p className="mt-3 text-sm text-zinc-400">Authorized personnel only.</p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Desk email" className={inputCls} data-testid="desk-input-email" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passcode" className={inputCls} data-testid="desk-input-password" />
            {error && <p className="text-sm text-red-400" data-testid="desk-login-error">{error}</p>}
            <button type="submit" data-testid="desk-login-submit"
              className="w-full py-3.5 text-[12px] uppercase tracking-[0.2em] font-medium text-[#0A0A0B] bg-gradient-to-r from-[#C9A227] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#E8C85A] transition-colors">
              Access Desk
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="desk-dashboard" className="bg-[#0A0A0B] min-h-screen pt-36 pb-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow>Private Desk</Eyebrow>
            <h1 className="font-display text-2xl sm:text-3xl text-white tracking-wide">Incoming Mandates</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load(token)} data-testid="desk-refresh"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] text-zinc-400 card-line hover:text-[#D4AF37] transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); }} data-testid="desk-logout"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] text-zinc-400 card-line hover:text-red-400 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        {!mandates ? (
          <p className="mt-12 text-zinc-400 text-sm">Loading…</p>
        ) : mandates.length === 0 ? (
          <p className="mt-12 text-zinc-400 text-sm" data-testid="desk-empty">No mandates yet.</p>
        ) : (
          <div className="mt-10 space-y-4" data-testid="desk-list">
            {mandates.map((m) => (
              <div key={m.id} data-testid={`mandate-row-${m.reference}`} className="card-line bg-[#121215] p-6 grid md:grid-cols-4 gap-5 items-start">
                <div>
                  <div className="font-mono2 text-[#E8C85A] tracking-[0.12em]">{m.reference}</div>
                  <div className="mt-1 text-[11px] text-zinc-400">{new Date(m.created_at).toUTCString().slice(0, 22)}</div>
                  <span className={`mt-2 inline-block font-mono2 text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[m.status] || "text-zinc-300"}`} data-testid={`mandate-status-${m.reference}`}>
                    {(m.status || "received").replace("_", " ")}
                  </span>
                </div>
                <div className="text-sm">
                  <div className="text-zinc-200">{m.name}{m.company ? ` · ${m.company}` : ""}</div>
                  <div className="text-zinc-400">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                  <div className="text-zinc-400">{m.country || ""}</div>
                </div>
                <div className="text-sm text-zinc-400">
                  <div><span className="text-zinc-400">Role:</span> <span className="capitalize">{m.role}</span></div>
                  <div><span className="text-zinc-400">Commodity:</span> {m.commodity}</div>
                  <div><span className="text-zinc-400">Volume:</span> {m.volume || "—"}</div>
                  {m.details?.instrument && <div><span className="text-zinc-400">Instrument:</span> {m.details.instrument}</div>}
                  {m.details?.origin && <div><span className="text-zinc-400">Origin:</span> {m.details.origin}</div>}
                  {m.details?.timeline && <div><span className="text-zinc-400">Timeline:</span> {m.details.timeline}</div>}
                </div>
                <div>
                  <label className="block font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Status</label>
                  <select value={m.status || "received"} onChange={(e) => setStatus(m.id, e.target.value)}
                    data-testid={`status-select-${m.reference}`} className={inputCls}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
